import { lazy } from 'react';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import type { ModuleManifest } from '../../lib/host/types';
import { billingKeys } from './queries/keys';

// Billing module (P18 §1, story 1F-1) — the first feature retrofitted onto the
// module host, proving the pluggability contract: nothing outside this folder
// imports it, so deleting src/modules/billing/ leaves the platform compiling
// (the host globs manifests). The shared paywall/usage-meter components stay in
// components/billing/ because other features consume them; only the billing
// management surface is the module.
export const manifest: ModuleManifest = {
  key: 'billing',
  // Seeded into the feature catalog in 1F-2 (flag-everything). Unknown keys
  // default to 'on', so billing stays visible until then.
  featureKeys: ['billing.manage'],
  nav: [
    {
      key: 'billing',
      slot: 'main',
      label: 'Billing & plan',
      to: '/app/billing',
      icon: BanknotesIcon,
      permission: 'org:billing:manage',
      order: 70,
    },
  ],
  routes: [
    {
      path: '/app/billing',
      element: lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage }))),
      chrome: 'shell',
      // Same gate as the nav row above (story 2-20): before this, the route
      // mounted for every member while only the row was permission-gated, so a
      // non-billing persona could dead-end on the page by URL / stale link.
      permission: 'org:billing:manage',
      // Administration family (story 2-146d, P10 §2/§4) — composed onto
      // PageHeader + section rhythm.
      layoutFamily: 'administration',
    },
    {
      // Dedicated post-checkout success return (story 2-21): Stripe redirects
      // here (?session_id=…); the page always runs confirm+sync and surfaces
      // the outcome. Formerly pinned-exempt in the OnboardingTour by
      // construction; that tour is retired (story 2-106), and this return
      // still always runs unconditionally regardless.
      path: '/app/checkout/success',
      element: lazy(() =>
        import('./pages/CheckoutReturnPage').then((m) => ({ default: m.CheckoutReturnPage }))
      ),
      chrome: 'shell',
      // Only the persona who could START a checkout can land on its return.
      permission: 'org:billing:manage',
      // Administration family (story 2-146d) — a billing sub-surface, same
      // composition as /app/billing above.
      layoutFamily: 'administration',
    },
  ],
  agentTools: [],
  queryKeys: billingKeys,
};
